/**
 * The concierge specialist roster — Tommy plus his four teammates.
 *
 * One support desk, five faces: every customer conversation lives in the
 * same tommy_threads/tommy_messages rails regardless of who's speaking,
 * and the specialist on a thread can change between visits (like any real
 * front desk between shifts). resolveSpecialist below holds the selection
 * rules; the module is shared so client and server agree on the roster.
 *
 * All five run the identical persona, prompt, knowledge base, and consult
 * flow (they are deliberate replicas — see buildTommySystemPrompt's
 * personaName parameter). Only the name and the face differ.
 *
 * Portraits: Tommy's is /tommy.jpg (2:3, the founder's pick of 2026-10-30;
 * the square /staff/tommy.png is cropped from the same image). The other
 * four live in /staff/<id>.png alongside the Staff-page portraits
 * (scripts/generate-staff-portrait.mjs house style).
 */

export const CONCIERGE_SPECIALISTS = [
  { id: 'tommy', name: 'Tommy', avatar: '/tommy.jpg', avatarPosition: 'center 33%' },
  { id: 'camille', name: 'Camille', avatar: '/staff/camille.png', avatarPosition: 'center' },
  { id: 'andre', name: 'Andre', avatar: '/staff/andre.png', avatarPosition: 'center' },
  { id: 'isabella', name: 'Isabella', avatar: '/staff/isabella.png', avatarPosition: 'center' },
  { id: 'kai', name: 'Kai', avatar: '/staff/kai.png', avatarPosition: 'center' },
];

const BY_ID = new Map(CONCIERGE_SPECIALISTS.map((s) => [s.id, s]));

/** Look up a specialist; unknown/missing ids resolve to Tommy so nothing
 *  downstream ever renders a nameless, faceless reply. */
export function getSpecialist(id) {
  return BY_ID.get(String(id || '').toLowerCase()) || CONCIERGE_SPECIALISTS[0];
}

export function isSpecialistId(id) {
  return BY_ID.has(String(id || '').toLowerCase());
}

export function pickRandomSpecialist() {
  return CONCIERGE_SPECIALISTS[Math.floor(Math.random() * CONCIERGE_SPECIALISTS.length)];
}

/** A conversation currently in flight keeps its specialist; a customer
 *  coming back later may find someone else at the desk. */
export const SPECIALIST_STICKY_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * Who speaks on this thread right now.
 *
 *   - Mid-conversation (a message within the sticky window) → the same
 *     specialist stays; swapping voices mid-thread reads as a glitch.
 *   - Fresh visit (stale thread or brand-new) → an explicit valid hint
 *     wins (the opener's pick, so the header face and the reply always
 *     match), else random.
 */
export function resolveSpecialist({ currentId = null, lastMessageAt = null, hintId = null, now = Date.now() } = {}) {
  const last = lastMessageAt ? new Date(lastMessageAt).getTime() : null;
  const active = Number.isFinite(last) && now - last < SPECIALIST_STICKY_WINDOW_MS;
  if (active && isSpecialistId(currentId)) return getSpecialist(currentId);
  if (isSpecialistId(hintId)) return getSpecialist(hintId);
  return pickRandomSpecialist();
}
